import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Plus, Trash2 } from "lucide-react";

// Admin-only pricing editor: edits the live DSS AI plans shown on the landing page.
export default function PlansEditor() {
  const [plans, setPlans] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    api.get("/plans").then((r) => {
      setPlans(r.data.map((p) => ({ ...p, featuresText: (p.features || []).join(", ") })));
      setLoaded(true);
    }).catch(() => setLoaded(true));
  }, []);

  const update = (i, field, value) => {
    setPlans((prev) => prev.map((p, idx) => (idx === i ? { ...p, [field]: value } : p)));
  };

  const addPlan = () => {
    setPlans((prev) => [...prev, { id: `plan-${Date.now().toString(36)}`, name: "", price: "", tagline: "", featuresText: "", active: true }]);
  };

  const removePlan = (i) => setPlans((prev) => prev.filter((_, idx) => idx !== i));

  const save = async () => {
    setSaving(true);
    try {
      const payload = plans.map((p) => ({
        id: p.id || p.name,
        name: p.name,
        price: p.price,
        tagline: p.tagline,
        features: p.featuresText.split(",").map((f) => f.trim()).filter(Boolean),
        active: !!p.active,
        max_projects: parseInt(p.max_projects, 10) || 0,
        monthly_gens: parseInt(p.monthly_gens, 10) || 0,
      }));
      const { data } = await api.put("/plans", { plans: payload });
      setPlans(data.map((p) => ({ ...p, featuresText: (p.features || []).join(", ") })));
      toast.success("Plans updated — the landing page shows them immediately");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const inputCls = "w-full px-2.5 py-1.5 rounded-lg bg-white dark:bg-white/[0.04] border border-slate-200 dark:border-white/10 text-slate-900 dark:text-white text-xs focus:outline-none focus:border-cyan-600 dark:focus:border-cyan-400/60 transition-all";

  if (!loaded) return null;

  return (
    <div className="accent-bar premium-card rounded-xl bg-white dark:bg-slate-900/80 border border-slate-200 dark:border-white/[0.08] overflow-hidden" data-testid="plans-editor-card">
      <div className="flex items-center justify-between p-4 border-b border-slate-200 dark:border-white/[0.06]">
        <div className="text-xs uppercase code-font neon-text font-bold tracking-wider">Plans & Pricing (Admin)</div>
        <button onClick={addPlan} className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-300 text-[10px] font-bold uppercase tracking-wider hover:bg-slate-100 dark:hover:bg-white/[0.06] transition-colors" data-testid="plan-add-button">
          <Plus className="w-3 h-3" /> Add plan
        </button>
      </div>
      <div className="p-4 space-y-4">
        {plans.map((p, i) => (
          <div key={p.id} className="rounded-lg border border-slate-200 dark:border-white/[0.06] p-3 space-y-2" data-testid={`plan-editor-row-${p.id}`}>
            <div className="flex items-center gap-2">
              <input className={inputCls} placeholder="Plan name" value={p.name} onChange={(e) => update(i, "name", e.target.value)} data-testid={`plan-name-${p.id}`} />
              <input className={`${inputCls} max-w-[110px]`} placeholder="₹499/mo" value={p.price} onChange={(e) => update(i, "price", e.target.value)} data-testid={`plan-price-input-${p.id}`} />
              <label className="flex items-center gap-1.5 text-[10px] code-font uppercase text-slate-500 dark:text-slate-400 shrink-0">
                <input type="checkbox" checked={!!p.active} onChange={(e) => update(i, "active", e.target.checked)} data-testid={`plan-active-${p.id}`} />
                Live
              </label>
              <button onClick={() => removePlan(i)} className="p-1.5 rounded-lg text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-500/10 transition-colors" data-testid={`plan-delete-${p.id}`} title="Remove plan">
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
            <input className={inputCls} placeholder="Tagline (optional)" value={p.tagline} onChange={(e) => update(i, "tagline", e.target.value)} data-testid={`plan-tagline-${p.id}`} />
            <div className="flex gap-2">
              <label className="flex-1">
                <span className="block text-[9px] code-font uppercase text-slate-400 dark:text-slate-500 mb-1">Max projects (0 = unlimited)</span>
                <input type="number" min="0" className={inputCls} value={p.max_projects ?? 0} onChange={(e) => update(i, "max_projects", e.target.value)} data-testid={`plan-max-projects-${p.id}`} />
              </label>
              <label className="flex-1">
                <span className="block text-[9px] code-font uppercase text-slate-400 dark:text-slate-500 mb-1">AI drafts / month (0 = unlimited)</span>
                <input type="number" min="0" className={inputCls} value={p.monthly_gens ?? 0} onChange={(e) => update(i, "monthly_gens", e.target.value)} data-testid={`plan-monthly-gens-${p.id}`} />
              </label>
            </div>
            <input className={inputCls} placeholder="Features, comma separated" value={p.featuresText} onChange={(e) => update(i, "featuresText", e.target.value)} data-testid={`plan-features-${p.id}`} />
          </div>
        ))}
        <button
          onClick={save}
          disabled={saving}
          className="w-full px-4 py-2.5 rounded-xl bg-gradient-to-r from-cyan-600 to-sky-600 dark:from-cyan-400 dark:to-sky-400 text-white dark:text-[#090D16] font-bold uppercase text-xs tracking-wider hover:from-cyan-700 hover:to-sky-700 dark:hover:from-cyan-300 dark:hover:to-sky-300 shadow-md shadow-cyan-600/20 dark:shadow-cyan-400/20 transition-all disabled:opacity-60"
          data-testid="plans-save-button"
        >
          {saving ? "Saving…" : "Save Plans"}
        </button>
      </div>
    </div>
  );
}
